import React, { FunctionComponent } from 'react';
import ChangeTimeZoneDialog from './components/ChangeTimeZoneDialog/ChangeTimeZoneDialog';
import ChangeWeightUnitDialog from './components/ChangeWeightUnitDialog/ChangeWeightUnitDialog';
import ArchiveDevice from './components/ArchiveDevice/ArchiveDevice';
import ConfirmationDialog from '../../eliot-components/src/ConfirmationDialog/ConfirmationDialog';
import ConfigureSyncDialog from './components/ConfigureSyncDialog/ConfigureSyncDialog';
import ResetTokenDialog from './components/ResetTokenDialog/ResetTokenDialog';
import ChangeTargetSystemsDialog from './components/ChangeTargetSystemsDialog/ChangeTargetSystemsDialog';
import TransferOwnershipDialog from './components/TransferOwnership/TransferOwnershipDialog';
import { FilterDialog } from './components/ChangeMinimumWeightFilter/ChangeMinimumWeightFilter';
import FlagDeviceDialog from './components/FlagDeviceDialog/FlagDeviceDialog';
import DeviceLinking from './components/DeviceLinking/DeviceLinking';
import { useSnackbar } from 'notistack';
import { useConfig } from '../../eliot-components/src/ConfigProvider/hooks';
import {
  apiInvalidateTags,
  usePinDeviceMutation,
  useBlockUnblockDeviceMutation,
} from '../../store/api-slice';
import { IDialogState } from './types';
import { useAppSelector } from '../../hooks';
import { MedisanteDevice } from '../../pages/Device/types';
import DeleteDevice from './components/DeleteDevice/DeleteDevice';
import MuteDevice from './components/MuteDevice/MuteDevice';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import ResetMeasurementCount from './components/ResetMeasurementCount/ResetMeasurementCount';

export interface Props {
  readonly dialogState: IDialogState;
  updateDialogState: (action: string) => void;
  readonly actualDevices: MedisanteDevice[];
}

const ActionMenuDialogs: FunctionComponent<Props> = ({
  updateDialogState,
  dialogState,
  actualDevices,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { apiBaseURL } = useConfig();
  const stateDevices: MedisanteDevice[] = useAppSelector(
    (state) => state.appState.actionMenuDevices
  );
  const stateDevicesIDs = stateDevices.map((d) => d.id);
  const devices = actualDevices.filter(
    (d) => stateDevicesIDs.indexOf(d.id) !== -1
  );
  const [pinDevice, { isLoading: isUpdating }] = usePinDeviceMutation();
  const [blockDevice, { isLoading: isUpdatingBlock }] =
    useBlockUnblockDeviceMutation();

  const closeDialog = () => updateDialogState('CLOSE_DIALOG');

  const isAnyUnpinned = !!(devices && devices.find((d) => !d.isPinned));

  const history = useHistory();
  const dispatch = useDispatch();

  const invalidateCache = (names: any) => {
    dispatch(apiInvalidateTags(names));
  };

  return (
    <>
      {!!dialogState.isChangeTimeZoneDialogOpen && (
        <ChangeTimeZoneDialog
          devices={devices}
          isOpen={true}
          onClose={closeDialog}
        />
      )}
      {!!dialogState.isChangeWeightUnitDialogOpen && (
        <ChangeWeightUnitDialog
          devices={devices}
          isOpen={true}
          onClose={closeDialog}
        />
      )}

      {!!dialogState.isArchiveDialogOpen && (
        <ArchiveDevice devices={devices} isOpen={true} onClose={closeDialog} />
      )}

      {!!dialogState.isDeleteDeviceDialogOpen && (
        <DeleteDevice
          devices={devices}
          isOpen={true}
          onClose={closeDialog}
          onCompleted={(errors) => {
            closeDialog();
            if (errors.length > 0) {
              for (const e of errors) {
                enqueueSnackbar(e, { variant: 'error' });
              }
              return;
            }
            enqueueSnackbar('Device deleted', { variant: 'info' });
            history.push('/devices');
            invalidateCache(['Devices']);
          }}
        />
      )}

      {!!dialogState.isMuteDialogOpen && (
        <MuteDevice
          devices={devices}
          isOpen={true}
          onClose={closeDialog}
          onUpdated={(err) => {
            closeDialog();
            if (err) {
              enqueueSnackbar(err, { variant: 'error' });
              return;
            }
            enqueueSnackbar('Device mute status changed', { variant: 'info' });
          }}
        />
      )}

      <ConfirmationDialog
        isOpen={!!dialogState.isPinDialogOpen}
        onClose={closeDialog}
        onConfirm={() => {
          if (pinDevice) {
            return pinDevice({
              apiBaseURL: apiBaseURL,
              orgId: devices[0].owner,
              deviceIDs: devices.map((d) => d.id),
              isPinned: isAnyUnpinned,
            });
          }
        }}
        isLoading={isUpdating}
        questionText={`Are you sure you want ${
          devices && devices.find((d) => !d.isPinned) ? 'pin' : 'unpin'
        } this device ?`}
        confirmationText="Confirm"
        cancelText="Cancel"
        title={`${
          devices && devices.find((d) => !d.isPinned) ? 'Pin' : 'Unpin'
        } device`}
      />
      {!!dialogState.isConfigureSyncDialogOpen && (
        <ConfigureSyncDialog
          devices={devices}
          isOpen={true}
          onClose={closeDialog}
        />
      )}
      {!!dialogState.isResetTokenDialogOpen && (
        <ResetTokenDialog
          devices={devices}
          isOpen={true}
          onClose={closeDialog}
        />
      )}
      {!!dialogState.isChangeTargetSystemsDialogOpen && (
        <ChangeTargetSystemsDialog
          devices={devices}
          isOpen={true}
          onClose={closeDialog}
        />
      )}
      {!!dialogState.isTransferOwnershipDialogOpen && (
        <TransferOwnershipDialog
          devices={devices}
          isOpen={true}
          onClose={closeDialog}
        />
      )}
      {!!dialogState.isWeightFilterDialogVisible && (
        <FilterDialog
          devices={devices}
          open={true}
          onClose={closeDialog}
          onSave={() => {
            closeDialog();
            enqueueSnackbar('Weight filter changed', { variant: 'info' });
          }}
        />
      )}
      {!!dialogState.isFlagDeviceDialogOpen && (
        <FlagDeviceDialog
          devices={devices}
          isOpen={true}
          onClose={closeDialog}
        />
      )}
      {!!dialogState.isDeviceLinkingDialogOpen && (
        <DeviceLinking
          initialDevices={devices}
          open={true}
          onClose={closeDialog}
        />
      )}
      {!!dialogState.isResetCountDialogOpen && (
        <ResetMeasurementCount
          devices={devices}
          isOpen={true}
          onClose={closeDialog}
          onCreated={(success, err) => {
            if (!success) {
              enqueueSnackbar(err, { variant: 'error' });
              return;
            }
            enqueueSnackbar('Measurement count reset', { variant: 'info' });
          }}
        />
      )}

      <ConfirmationDialog
        isOpen={!!dialogState.isBlockDeviceDialogOpen}
        onClose={closeDialog}
        onConfirm={() => {
          const toBlock = devices && devices.find((d) => !d.deactivated);
          if (blockDevice && toBlock) {
            return blockDevice({
              apiBaseURL: apiBaseURL,
              devices: devices,
              block: true,
            });
          } else if (blockDevice && !toBlock) {
            return blockDevice({
              apiBaseURL: apiBaseURL,
              devices: devices,
              block: false,
            });
          }
        }}
        isLoading={isUpdatingBlock}
        questionText={`Are you sure you want ${
          devices && devices.find((d) => d.deactivated) ? 'unblock' : 'block'
        } this device ?`}
        confirmationText="Confirm"
        cancelText="Cancel"
        title={`${
          devices && devices.find((d) => d.deactivated) ? 'Unblock' : 'Block'
        } device`}
      />
    </>
  );
};

export default ActionMenuDialogs;
